import React, { useRef } from "react";
import { X, Loader2 } from "lucide-react";
import { DEPARTMENTS, ROLES, EMPLOYMENT_TYPE } from "../../constants/hrConstants";

const Field = ({ label, error, children }) => (
  <div>
    <label className="label">{label}</label>
    {children}
    {error && <p className="text-red-500 text-[11px] mt-1">{error}</p>}
  </div>
);

const Select = ({ value, onChange, options, placeholder, error }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className={`input ${error ? "border-red-400 focus:ring-red-300" : ""}`}
  >
    <option value="">{placeholder}</option>
    {options.map((o) => (
      <option key={o.value} value={o.value}>{o.label}</option>
    ))}
  </select>
);

export function EmployeeFormModal({ isOpen, onClose, formData, formErrors, onFieldChange, onSubmit, isEditing, isSaving }) {
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  const empTypeOptions = Object.entries(EMPLOYMENT_TYPE).map(([, v]) => ({
    value: v,
    label: v.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase()),
  }));

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-[15px] font-bold text-slate-800">
              {isEditing ? "Edit Employee" : "Add New Employee"}
            </h2>
            <p className="text-[12px] text-slate-400 mt-0.5">
              {isEditing ? "Update employee information" : "Fill in the details to onboard a new employee"}
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {/* Personal Info */}
          <div>
            <h3 className="text-[12px] font-semibold text-slate-500 uppercase tracking-wider mb-3">Personal Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <Field label="First Name *" error={formErrors.firstName}>
                <input
                  type="text" className={`input ${formErrors.firstName ? "border-red-400" : ""}`}
                  placeholder="e.g. Ravi" value={formData.firstName || ""}
                  onChange={(e) => onFieldChange("firstName", e.target.value)}
                />
              </Field>
              <Field label="Last Name *" error={formErrors.lastName}>
                <input
                  type="text" className={`input ${formErrors.lastName ? "border-red-400" : ""}`}
                  placeholder="e.g. Kumar" value={formData.lastName || ""}
                  onChange={(e) => onFieldChange("lastName", e.target.value)}
                />
              </Field>
              <Field label="Phone *" error={formErrors.phone}>
                <input
                  type="tel" className={`input ${formErrors.phone ? "border-red-400" : ""}`}
                  placeholder="10-digit mobile" value={formData.phone || ""}
                  onChange={(e) => onFieldChange("phone", e.target.value)}
                />
              </Field>
              <Field label="Email *" error={formErrors.email}>
                <input
                  type="email" className={`input ${formErrors.email ? "border-red-400" : ""}`}
                  placeholder="email@company.com" value={formData.email || ""}
                  onChange={(e) => onFieldChange("email", e.target.value)}
                />
              </Field>
              <Field label="Employee ID *" error={formErrors.employeeId}>
                <input
                  type="text" className={`input ${formErrors.employeeId ? "border-red-400" : ""}`}
                  placeholder="e.g. 045" value={formData.employeeId || ""}
                  onChange={(e) => onFieldChange("employeeId", e.target.value)}
                />
              </Field>
              {!isEditing && (
                <Field label={isEditing ? "Password (Leave blank to keep)" : "Password *"} error={formErrors.password}>
                  <div>
                    <input 
                      type="text" className={`input ${formErrors.password ? "border-red-400" : ""}`}
                      placeholder={isEditing ? "Enter new password" : "Enter password"} value={formData.password || ""}
                      onChange={(e) => onFieldChange("password", e.target.value)}
                    />
                    <p className="text-[10px] text-slate-400 mt-1">Min 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 special character.</p>
                  </div>
                </Field>
              )}
              <Field label="Gender" error={formErrors.gender}>
                <Select
                  value={formData.gender || ""} onChange={(v) => onFieldChange("gender", v)}
                  options={[{label: "Male", value: "male"}, {label: "Female", value: "female"}, {label: "Other", value: "other"}]} placeholder="Select gender"
                />
              </Field>
              <Field label="Date of Birth" error={formErrors.dob}>
                <input
                  type="date" className="input"
                  value={formData.dob || ""} onChange={(e) => onFieldChange("dob", e.target.value)}
                />
              </Field>
              <Field label="Blood Group" error={formErrors.bloodGroup}>
                <input
                  type="text" className="input"
                  placeholder="e.g. O+" value={formData.bloodGroup || ""}
                  onChange={(e) => onFieldChange("bloodGroup", e.target.value)}
                />
              </Field>
              <Field label="Religion" error={formErrors.religion}>
                <input
                  type="text" className="input"
                  placeholder="e.g. Hindu" value={formData.religion || ""}
                  onChange={(e) => onFieldChange("religion", e.target.value)}
                />
              </Field>
            </div>
          </div>

          {/* Past Experience */}
          <div>
            <h3 className="text-[12px] font-semibold text-slate-500 uppercase tracking-wider mb-3">Past Experience</h3>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Previous Experience (Years)" error={formErrors.experienceYears}>
                <input
                  type="number" className="input"
                  placeholder="e.g. 5" value={formData.experienceYears || ""}
                  onChange={(e) => onFieldChange("experienceYears", e.target.value)}
                />
              </Field>
              <Field label="Previous Company" error={formErrors.previousCompany}>
                <input
                  type="text" className="input"
                  placeholder="e.g. TechCorp" value={formData.previousCompany || ""}
                  onChange={(e) => onFieldChange("previousCompany", e.target.value)}
                />
              </Field>
            </div>
          </div>

          {/* Job Info */}
          <div>
            <h3 className="text-[12px] font-semibold text-slate-500 uppercase tracking-wider mb-3">Job Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Designation *" error={formErrors.designation}>
                <input
                  type="text" className={`input ${formErrors.designation ? "border-red-400" : ""}`}
                  placeholder="e.g. Senior Engineer" value={formData.designation || ""}
                  onChange={(e) => onFieldChange("designation", e.target.value)}
                />
              </Field>

              <Field label="Department *" error={formErrors.department}>
                <Select
                  value={formData.department || ""} onChange={(v) => onFieldChange("department", v)}
                  options={DEPARTMENTS} placeholder="Select department" error={formErrors.department}
                />
              </Field>
              <Field label="Role *" error={formErrors.role}>
                <Select
                  value={formData.role || ""} onChange={(v) => onFieldChange("role", v)}
                  options={ROLES} placeholder="Select role" error={formErrors.role}
                />
              </Field>
              <Field label="Employment Type *" error={formErrors.employmentType}>
                <Select
                  value={formData.employmentType || "full_time"} onChange={(v) => onFieldChange("employmentType", v)}
                  options={empTypeOptions} placeholder="Select type" error={formErrors.employmentType}
                />
              </Field>
              <Field label="Joining Date *" error={formErrors.joiningDate}>
                <input
                  type="date" className={`input ${formErrors.joiningDate ? "border-red-400" : ""}`}
                  value={formData.joiningDate || ""} onChange={(e) => onFieldChange("joiningDate", e.target.value)}
                />
              </Field>
              <Field label="CTC (₹/year) *" error={formErrors.salary}>
                <div className="relative">
                  <input
                    type="number" className={`input pr-20 ${formErrors.salary ? "border-red-400" : ""}`}
                    placeholder="e.g. 600000" value={formData.salary || ""}
                    onChange={(e) => onFieldChange("salary", e.target.value)}
                  />
                  {formData.salary > 0 && (
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                      <span className="text-[11px] font-bold bg-blue-50 text-blue-600 px-2 py-0.5 rounded-md border border-blue-100">
                        {(Number(formData.salary) / 100000).toFixed(1)} LPA
                      </span>
                    </div>
                  )}
                </div>
              </Field>
              <Field label="Work Location *" error={formErrors.workLocation}>
                <input
                  type="text" className={`input ${formErrors.workLocation ? "border-red-400" : ""}`}
                  placeholder="e.g. Hyderabad" value={formData.workLocation || ""}
                  onChange={(e) => onFieldChange("workLocation", e.target.value)}
                />
              </Field>
              <Field label="Reporting Manager" error={formErrors.reportingManager}>
                <input
                  type="text" className="input"
                  placeholder="Manager name" value={formData.reportingManager || ""}
                  onChange={(e) => onFieldChange("reportingManager", e.target.value)}
                />
              </Field>
            </div>
          </div>

          {/* Bank Details */}
          <div>
            <h3 className="text-[12px] font-semibold text-slate-500 uppercase tracking-wider mb-3">Bank Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Bank Name" error={formErrors.bankName}>
                <input
                  type="text" className={`input ${formErrors.bankName ? "border-red-400" : ""}`}
                  placeholder="e.g. HDFC Bank" value={formData.bankName || ""}
                  onChange={(e) => onFieldChange("bankName", e.target.value)}
                />
              </Field>
              <Field label="Account Holder Name" error={formErrors.accountHolderName}>
                <input
                  type="text" className={`input ${formErrors.accountHolderName ? "border-red-400" : ""}`}
                  placeholder="As per bank record" value={formData.accountHolderName || ""}
                  onChange={(e) => onFieldChange("accountHolderName", e.target.value)}
                />
              </Field>
              <Field label="Bank Account Number" error={formErrors.accountNumber}>
                <input
                  type="text" className={`input ${formErrors.accountNumber ? "border-red-400" : ""}`}
                  placeholder="e.g. 50100012345678" value={formData.accountNumber || ""}
                  onChange={(e) => onFieldChange("accountNumber", e.target.value)}
                />
              </Field>
              <Field label="IFSC Code" error={formErrors.ifscCode}>
                <input
                  type="text" className={`input uppercase ${formErrors.ifscCode ? "border-red-400" : ""}`}
                  placeholder="e.g. HDFC0001234" value={formData.ifscCode || ""}
                  onChange={(e) => onFieldChange("ifscCode", e.target.value.toUpperCase())}
                />
              </Field>
              <Field label="Branch Name" error={formErrors.branchName}>
                <input
                  type="text" className={`input ${formErrors.branchName ? "border-red-400" : ""}`}
                  placeholder="e.g. Jubilee Hills Branch" value={formData.branchName || ""}
                  onChange={(e) => onFieldChange("branchName", e.target.value)}
                />
              </Field>
              <Field label="Account Type" error={formErrors.accountType}>
                <select
                  value={formData.accountType || "savings"}
                  onChange={(e) => onFieldChange("accountType", e.target.value)}
                  className="input text-slate-700"
                >
                  <option value="savings">Savings Account</option>
                  <option value="current">Current Account</option>
                </select>
              </Field>
            </div>
          </div>

          {/* Documents */}
          <div>
            <h3 className="text-[12px] font-semibold text-slate-500 uppercase tracking-wider mb-3">Documents</h3>
            <Field label="Upload Employee Documents (Multiple)" error={formErrors.documents}>
              <div
                className="input py-1.5 text-sm cursor-pointer flex items-center gap-2 hover:bg-slate-50 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <span className="file:mr-4 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 px-3 py-1 rounded-md bg-blue-50 text-blue-700 text-xs font-semibold">
                  Choose Files
                </span>
                <span className="text-slate-500 text-xs">
                  {formData.documents?.length > 0
                    ? `${formData.documents.length} file${formData.documents.length > 1 ? "s" : ""} selected`
                    : "No file chosen"}
                </span>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => {
                  const files = Array.from(e.target.files);
                  const newDocs = files.map(file => ({
                    name: file.name,
                    url: URL.createObjectURL(file),
                    file
                  }));
                  onFieldChange("documents", [...(formData.documents || []), ...newDocs]);
                  e.target.value = "";
                }}
              />
            </Field>
            {formData.documents?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {formData.documents.map((doc, idx) => (
                  <div key={idx} className="flex items-center gap-1.5 bg-slate-50 text-slate-700 text-xs font-medium px-2.5 py-1.5 rounded-md border border-slate-200 shadow-sm">
                    <span className="truncate max-w-[150px]" title={doc.name}>{doc.name}</span>
                    <button 
                      type="button"
                      onClick={() => {
                        const newDocs = formData.documents.filter((_, i) => i !== idx);
                        onFieldChange("documents", newDocs);
                      }}
                      className="text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md p-0.5 transition-colors"
                      title="Remove Document"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Notes */}
          <Field label="Notes" error={formErrors.notes}>
            <textarea
              className="input h-20 py-2 resize-none"
              placeholder="Any additional notes..."
              value={formData.notes || ""}
              onChange={(e) => onFieldChange("notes", e.target.value)}
            />
          </Field>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex justify-end gap-3 sticky bottom-0">
          <button onClick={onClose} className="btn-secondary" disabled={isSaving}>Cancel</button>
          <button onClick={onSubmit} className="btn-primary min-w-[110px]" disabled={isSaving}>
            {isSaving ? (
              <span className="flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving...</span>
            ) : isEditing ? "Update Employee" : "Add Employee"}
          </button>
        </div>
      </div>
    </div>
  );
}
